import { useState } from "react";
import { useDebounce } from "@/hooks/useDebounce";
import { useIsMobile } from "@/hooks/useMediaQuery";
// useSupabaseReportGroups.ts was removed with the SQL Server fallback (47961b4);
// the Supabase-only hooks have identical signatures.
import {
  useSupabaseReportGroupsOnly as useSupabaseReportGroups,
  useReportGroupStatsOnly as useReportGroupStats,
} from "../hooks/useSupabaseReportGroupsOnly";
import { useCompanyNameByLegacyId } from "../hooks/useSupabaseCompanies";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Search, Users, Building2, Hash, FileText, RefreshCw, Database, BarChart3, Menu } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import PageLoader from "@/components/PageLoader";
import PageTransition from "@/components/PageTransition";

// Company name display component
const CompanyDisplay = ({ compId }: { compId?: string }) => {
  const companyName = useCompanyNameByLegacyId(compId);
  
  if (!compId) {
    return <span className="text-muted-foreground">-</span>;
  }
  
  return (
    <Badge variant="secondary" className="flex items-center gap-1">
      <Building2 className="h-3 w-3" />
      {companyName}
    </Badge>
  );
};

// Mobile group card component
const MobileGroupCard = ({ group, onSelect }: { group: any; onSelect: (group: any) => void }) => {
  return (
    <Card 
      className="mb-4 cursor-pointer hover:shadow-md transition-shadow" 
      onClick={() => onSelect(group)}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2 flex-1">
            {group.icon ? (
              <Avatar className="h-8 w-8">
                <AvatarImage src={group.icon} alt="Group Icon" />
                <AvatarFallback>
                  <FileText className="h-4 w-4" />
                </AvatarFallback>
              </Avatar>
            ) : (
              <div className="p-2 bg-blue-100 rounded-full">
                <FileText className="h-4 w-4 text-blue-600" />
              </div>
            )}
            <div className="flex-1">
              <h3 className="font-medium text-base">
                {group.group_name || <span className="text-muted-foreground italic">No name</span>}
              </h3>
              {group.description && (
                <p className="text-sm text-muted-foreground mt-1">{group.description}</p>
              )}
            </div>
          </div>
          <Badge variant="outline" className="ml-2">
            {group.legacy_id}
          </Badge>
        </div>
        
        <div className="flex flex-wrap gap-2 mt-3">
          {group.comp_id && (
            <CompanyDisplay compId={group.comp_id} />
          )}
          {group.unit_id && (
            <Badge variant="secondary" className="text-xs">
              <Building2 className="h-3 w-3 mr-1" />
              Unit: {group.unit_id}
            </Badge>
          )}
          {group.stt && (
            <Badge variant="outline" className="text-xs">
              <Hash className="h-3 w-3 mr-1" />
              #{group.stt}
            </Badge>
          )}
          {group.user_ids && (
            <Badge variant="outline" className="text-xs">
              <Users className="h-3 w-3 mr-1" />
              {group.user_ids.split(',').length} users
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const ReportGroups = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<any>(null);
  const debouncedSearchTerm = useDebounce(searchTerm, 300); // 300ms debounce
  const isMobile = useIsMobile();
  
  // Use Supabase report groups with debounced search filter
  const { 
    data: reportGroupsResponse, 
    isLoading: loading, 
    error, 
    refetch 
  } = useSupabaseReportGroups(
    { search: debouncedSearchTerm },
    { limit: 100 }
  );

  // Get report group statistics
  const { 
    data: stats, 
    isLoading: statsLoading 
  } = useReportGroupStats();

  // Extract report groups from response
  const reportGroups = reportGroupsResponse?.data || [];
  const filteredReportGroups = reportGroups; // Already filtered by search on server

  // Handle group selection
  const handleGroupSelect = (group: any) => {
    setSelectedGroup(group);
    if (isMobile) {
      setIsSheetOpen(false); // Auto-hide sheet trên mobile
    }
    // TODO: Navigate to reports page for this group
    console.log('Selected group:', group);
  };

  if (loading) {
    return <PageLoader type="table" title="Đang tải dữ liệu report groups..." />;
  }

  if (error) {
    return (
      <PageTransition>
        <div className="container mx-auto py-6">
          <Alert variant="destructive">
            <AlertDescription>
              Error loading report groups: {error?.message || String(error)}
              <Button variant="outline" size="sm" onClick={() => refetch()} className="ml-2">
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <div className="container mx-auto py-6">
        {/* Statistics Cards */}
        {stats && !statsLoading && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-full">
                    <Database className="h-4 w-4 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-muted-foreground">
                      Total Groups
                    </p>
                    <p className="text-2xl font-bold">{stats.totalGroups}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-full">
                    <FileText className="h-4 w-4 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-muted-foreground">
                      With Icons
                    </p>
                    <p className="text-2xl font-bold">{stats.groupsWithIcons}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center">
                  <div className="p-2 bg-purple-100 rounded-full">
                    <BarChart3 className="h-4 w-4 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-muted-foreground">
                      Companies
                    </p>
                    <p className="text-2xl font-bold">{Object.keys(stats.groupsByCompany).length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Mobile Layout - Sheet */}
        {isMobile ? (
          <div className="space-y-4">
            {/* Header với trigger để mở sheet */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-xl font-bold flex items-center gap-2">
                      <Database className="h-5 w-5" />
                      Report Groups
                    </CardTitle>
                    <CardDescription>
                      {filteredReportGroups.length} groups available
                    </CardDescription>
                  </div>
                  
                  <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
                    <SheetTrigger asChild>
                      <Button variant="outline" size="sm">
                        <Menu className="h-4 w-4 mr-2" />
                        Select Group
                      </Button>
                    </SheetTrigger>
                    <SheetContent side="bottom" className="max-h-[80vh]">
                      <SheetHeader>
                        <SheetTitle>Chọn nhóm báo cáo</SheetTitle>
                        <SheetDescription>
                          Chọn một nhóm để xem danh sách báo cáo
                        </SheetDescription>
                        
                        {/* Search trong sheet */}
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                          <Input
                            placeholder="Tìm kiếm nhóm báo cáo..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10"
                          />
                        </div>
                      </SheetHeader>
                      
                      <div className="px-4 pb-4 overflow-y-auto">
                        {filteredReportGroups.length > 0 ? (
                          filteredReportGroups.map((group) => (
                            <MobileGroupCard 
                              key={group.legacy_id || group.id}
                              group={group}
                              onSelect={handleGroupSelect}
                            />
                          ))
                        ) : (
                          <div className="text-center py-8 text-muted-foreground">
                            <FileText className="h-8 w-8 mx-auto mb-2" />
                            <p>Không tìm thấy nhóm báo cáo</p>
                            {searchTerm && (
                              <p className="text-sm mt-1">
                                Thử điều chỉnh từ khóa tìm kiếm
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </SheetContent>
                  </Sheet>
                </div>
                
                <Button variant="outline" size="sm" onClick={() => refetch()} className="w-fit">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </CardHeader>
            </Card>

            {/* Selected Group Info */}
            {selectedGroup && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Nhóm đã chọn</CardTitle>
                </CardHeader>
                <CardContent>
                  <MobileGroupCard 
                    group={selectedGroup}
                    onSelect={() => {}} // Không cần action khi đã chọn
                  />
                </CardContent>
              </Card>
            )}
          </div>
        ) : (
          /* Desktop Layout - Table */
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle className="text-2xl font-bold flex items-center gap-2">
                    <Database className="h-6 w-6" />
                    Report Groups
                  </CardTitle>
                  <CardDescription>
                    Manage report groups and their configurations (Loaded from Supabase)
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="w-fit">
                    {filteredReportGroups.length} groups
                  </Badge>
                  <Button variant="outline" size="sm" onClick={() => refetch()}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </div>
              
              {/* Search */}
              <div className="relative max-w-sm">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search report groups..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </CardHeader>

            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[100px]">ID</TableHead>
                      <TableHead>Group Name</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Icon</TableHead>
                      <TableHead>Unit ID</TableHead>
                      <TableHead>Company ID</TableHead>
                      <TableHead>Users</TableHead>
                      <TableHead>Order</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredReportGroups.length > 0 ? (
                      filteredReportGroups.map((group) => (
                        <TableRow 
                          key={group.legacy_id || group.id} 
                          className="hover:bg-muted/50 cursor-pointer"
                          onClick={() => handleGroupSelect(group)}
                        >
                          <TableCell className="font-mono text-sm">
                            <Badge variant="outline" className="font-mono">
                              {group.legacy_id}
                            </Badge>
                          </TableCell>
                          
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-blue-600" />
                              {group.group_name || (
                                <span className="text-muted-foreground italic">No name</span>
                              )}
                            </div>
                          </TableCell>
                          
                          <TableCell className="max-w-xs">
                            {group.description ? (
                              <span className="text-sm text-muted-foreground">
                                {group.description}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          
                          <TableCell>
                            {group.icon ? (
                              <div className="flex items-center gap-2">
                                <Avatar className="h-8 w-8">
                                  <AvatarImage src={group.icon} alt="Group Icon" />
                                  <AvatarFallback>
                                    <FileText className="h-4 w-4" />
                                  </AvatarFallback>
                                </Avatar>
                                <span className="text-sm text-muted-foreground truncate max-w-[100px]">
                                  {group.icon.substring(0, 20)}...
                                </span>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          
                          <TableCell>
                            {group.unit_id ? (
                              <Badge variant="secondary" className="flex items-center gap-1">
                                <Building2 className="h-3 w-3" />
                                {group.unit_id}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          
                          <TableCell>
                            <CompanyDisplay compId={group.comp_id} />
                          </TableCell>
                          
                          <TableCell className="max-w-xs">
                            {group.user_ids ? (
                              <div className="flex flex-wrap gap-1">
                                {group.user_ids.split(',').slice(0, 3).map((userId, index) => (
                                  <Badge key={index} variant="outline" className="text-xs flex items-center gap-1">
                                    <Users className="h-3 w-3" />
                                    {userId.trim()}
                                  </Badge>
                                ))}
                                {group.user_ids.split(',').length > 3 && (
                                  <Badge variant="outline" className="text-xs">
                                    +{group.user_ids.split(',').length - 3} more
                                  </Badge>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          
                          <TableCell>
                            {group.stt ? (
                              <Badge variant="outline" className="flex items-center gap-1">
                                <Hash className="h-3 w-3" />
                                {group.stt}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8">
                          <div className="flex flex-col items-center gap-2 text-muted-foreground">
                            <FileText className="h-8 w-8" />
                            <p>No report groups found</p>
                            {searchTerm && (
                              <p className="text-sm">
                                Try adjusting your search term
                              </p>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </PageTransition>
  );
};

export default ReportGroups;
